import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bath, Ruler, MapPin, Check, MessageCircle, Loader2 } from "lucide-react";
import { api, formatPrice, imgSrc, TYPES, waLink } from "@/lib/api";
import { StatusBadge } from "@/components/site/StatusBadge";
import { LeadForm } from "@/components/site/LeadForm";

const Gallery = ({ images, name }) => {
  const [active, setActive] = useState(0);
  if (!images.length) return <div className="aspect-[16/10] bg-[#2F2A28] rounded-sm" />;
  return (
    <div data-testid="programme-gallery">
      <div className="aspect-[16/10] overflow-hidden rounded-sm bg-[#2F2A28]">
        <img key={active} src={imgSrc(images[active])} alt={name} className="h-full w-full object-cover rise" data-testid="gallery-main-image" />
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3 overflow-x-auto">
          {images.map((src, i) => (
            <button key={src + i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
              className={`h-20 w-28 shrink-0 overflow-hidden rounded-sm border-2 transition-colors ${i === active ? "border-[#C88A58]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Specs = ({ p }) => {
  const items = [
    p.bedrooms != null && { icon: BedDouble, label: "Chambres", value: p.bedrooms },
    p.bathrooms != null && { icon: Bath, label: "Salles de bain", value: p.bathrooms },
    p.surface != null && { icon: Ruler, label: "Surface", value: `${p.surface} m²` },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="grid gap-px bg-[#2F2A28]/10 mt-10" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }} data-testid="programme-specs">
      {items.map(({ icon: Icon, label, value }) => (
        <div key={label} className="bg-[#FAF7F2] p-5">
          <Icon className="h-5 w-5 text-[#C88A58]" strokeWidth={1.4} />
          <p className="font-display text-3xl mt-3">{value}</p>
          <p className="text-xs text-[#756D69] uppercase tracking-wider">{label}</p>
        </div>
      ))}
    </div>
  );
};

export default function ProgrammeDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [all, setAll] = useState([]);

  useEffect(() => {
    setP(null);
    api.get(`/programmes/${id}`).then((r) => setP(r.data)).catch(() => setError(true));
    api.get("/programmes").then((r) => setAll(r.data)).catch(() => {});
  }, [id]);

  if (error) return <div className="max-w-7xl mx-auto px-5 py-32 font-display text-4xl" data-testid="programme-not-found">Programme introuvable.</div>;
  if (!p) return <div className="py-40 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#C88A58]" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="programme-detail-page">
      <Link to="/programmes" className="inline-flex items-center gap-2 text-sm text-[#756D69] hover:text-[#C88A58] transition-colors" data-testid="back-to-programmes-link">
        <ArrowLeft className="h-4 w-4" /> Tous les programmes
      </Link>
      <div className="grid lg:grid-cols-12 gap-12 mt-8">
        <div className="lg:col-span-7">
          <Gallery images={p.images} name={p.name} />
          <Specs p={p} />
          <div className="mt-12">
            <p className="eyebrow">Description</p>
            <p className="mt-4 text-[#4a4340] leading-relaxed whitespace-pre-line" data-testid="programme-description">{p.description}</p>
          </div>
          {p.features.length > 0 && (
            <ul className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="programme-features">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-[#1F3A2B]" />{f}</li>
              ))}
            </ul>
          )}
        </div>
        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-8">
          <div>
            <StatusBadge status={p.status} testId="programme-status" />
            <h1 className="font-display text-4xl sm:text-5xl mt-4 leading-tight" data-testid="programme-name">{p.name}</h1>
            <p className="flex items-center gap-2 text-[#756D69] mt-3 text-sm"><MapPin className="h-4 w-4" />{p.city} · {TYPES[p.type]}</p>
            <p className="font-display text-3xl text-[#C88A58] mt-6" data-testid="programme-price">{formatPrice(p.price, p.price_from)}</p>
            <a href={waLink(`Bonjour Hélium Résidences, je suis intéressé(e) par ${p.name}.`)} target="_blank" rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1fb658] text-white rounded-full text-sm transition-colors" data-testid="programme-whatsapp-button">
              <MessageCircle className="h-4 w-4" /> Demander des infos sur WhatsApp
            </a>
          </div>
          <div className="bg-white border border-[#2F2A28]/10 p-6 sm:p-8 rounded-sm">
            <p className="font-display text-2xl mb-6">Visite ou réservation</p>
            <LeadForm programmes={all} defaultProgrammeId={p.id} defaultKind="visite" />
          </div>
        </aside>
      </div>
    </div>
  );
}
